/** Who the explainer speaks to: the one who checks documents, or the one who brings them in. */
export type ExplainerAudience = 'reviewer' | 'scanner' | 'runner';

export interface ExplainerStep {
  /** The station's picture (lucide's name for it, drawn by TourExplainer). */
  art: 'arrive' | 'read' | 'check' | 'send';
  title: string;
  line: string;
}

/**
 * How Paige works, in four steps (gap list C10: an invited reviewer or scanner may not run the sample tour, which
 * makes a job). Said to the person: the step that is theirs says "you". A job is one stream of documents with many
 * learned types (Dustin, 2026-09-21), never one kind.
 */
export function explainerSteps(audience: ExplainerAudience): readonly ExplainerStep[] {
  return [
    {
      art: 'arrive',
      title: audience === 'scanner' ? 'You bring the pages in' : 'Pages arrive',
      line: 'From a scanner, email, an upload or the API. A scanned stack is split into documents for you.',
    },
    {
      art: 'read',
      title: 'Cortexa reads them',
      line: 'Where each document starts, what kind it is, and the values that matter.',
    },
    {
      art: 'check',
      title:
        audience === 'scanner' ? 'A reviewer checks the rest' : 'You check what it wasn’t sure of',
      line:
        audience === 'scanner'
          ? 'Anything Cortexa wasn’t sure of goes to Needs review.'
          : 'It comes to you under Needs review. Ctrl+Enter confirms it and opens the next.',
    },
    {
      art: 'send',
      title: 'Out it goes, and the job learns',
      line: 'Filed where your team works. Every fix teaches the job, so the next stack needs fewer.',
    },
  ];
}

/** The audience a member's role makes them: a scanner brings pages in, a reviewer checks, the rest run jobs. */
export function audienceOf(
  membershipRole: string | undefined,
  runsJobs: boolean,
): ExplainerAudience {
  if (membershipRole === 'scanner_operator') return 'scanner';
  return runsJobs ? 'runner' : 'reviewer';
}
